import { useState, useCallback } from "react";
import { integrationConnectUrl } from "../../api";

const INTEGRATIONS_META = [
  {
    provider: "notion",
    label: "Notion",
    description: "Sincroniza proyectos, páginas y bases de datos de tu workspace de Notion.",
    connectPath: "notion",
    logo: (
      <div style={{
        width: 40, height: 40, borderRadius: 8,
        background: "var(--surface)", border: "1.5px solid #e2e8f0",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontWeight: 900, fontSize: 22, color: "#1a1a1a",
        fontFamily: "Georgia, serif", flexShrink: 0,
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
      }}>N</div>
    ),
  },
  {
    provider: "microsoft",
    label: "Microsoft 365 (Calendario)",
    description: "Visualiza tu calendario de Outlook en modo solo lectura.",
    connectPath: "microsoft",
    logo: (
      <div style={{
        width: 40, height: 40, borderRadius: 8,
        background: "var(--surface)", border: "1.5px solid #e2e8f0",
        display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr",
        overflow: "hidden", flexShrink: 0,
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
      }}>
        <div style={{ background: "#f25022" }} />
        <div style={{ background: "#7fba00" }} />
        <div style={{ background: "#00a4ef" }} />
        <div style={{ background: "#ffb900" }} />
      </div>
    ),
  },
];

export default function IntegrationsModal({ onClose, integrations = [], currentUser, token, onDisconnect, onRefresh, onSync }) {
  const [disconnecting, setDisconnecting] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [lastSynced, setLastSynced] = useState({});

  const getConnected = (provider) =>
    integrations.find((i) => i.provider === provider) || null;

  const handleConnect = async (provider) => {
    try { window.location.href = await integrationConnectUrl(provider); }
    catch (e) { alert(e.message || "No se pudo iniciar la conexión"); }
  };

  const handleDisconnect = async (provider) => {
    setDisconnecting(provider);
    try {
      await onDisconnect(provider);
      if (onRefresh) onRefresh();
    } finally {
      setDisconnecting(null);
    }
  };

  const handleSync = async (provider) => {
    if (!onSync) return;
    setSyncing(provider);
    try {
      const result = await onSync(provider);
      setLastSynced(prev => ({ ...prev, [provider]: new Date() }));
      return result;
    } finally {
      setSyncing(null);
    }
  };

  return (
    <div style={{ fontFamily: "var(--font, system-ui, sans-serif)" }}>
      {/* Header */}
      <h3 style={{
        margin: "0 0 20px",
        fontWeight: 700,
        fontSize: 16,
        color: "var(--text)",
      }}>
        Integraciones
      </h3>

      {/* Cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {INTEGRATIONS_META.map(({ provider, label, description, connectPath, logo }) => {
          const conn = getConnected(provider);
          const isDisconnecting = disconnecting === provider;

          return (
            <div
              key={provider}
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: 12,
                padding: "16px 18px",
                boxShadow: "var(--shadow-sm, 0 1px 3px rgba(0,0,0,0.06))",
              }}
            >
              {/* Card top: logo + info + status */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                {logo}

                {/* Text info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{
                      fontWeight: 700, fontSize: 14,
                      color: "var(--text)",
                    }}>
                      {label}
                    </span>
                    {conn && (
                      <span style={{
                        background: "#dcfce7",
                        color: "#16a34a",
                        fontSize: 11,
                        fontWeight: 600,
                        padding: "2px 9px",
                        borderRadius: 99,
                        border: "1px solid var(--ok-line)",
                        lineHeight: 1.6,
                      }}>
                        Conectado
                      </span>
                    )}
                  </div>
                  <p style={{
                    margin: "4px 0 0",
                    fontSize: 12.5,
                    color: "var(--text3)",
                    lineHeight: 1.5,
                  }}>
                    {description}
                  </p>
                </div>
              </div>

              {/* Connected details */}
              {conn && (
                <div style={{
                  marginTop: 12,
                  padding: "10px 12px",
                  background: "var(--bg)",
                  borderRadius: 8,
                  border: "1px solid var(--border)",
                  fontSize: 12,
                  color: "var(--text2)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                }}>
                  {(conn.workspace_name) && (
                    <div>
                      <span style={{ color: "var(--text3)" }}>Cuenta: </span>
                      <span style={{ fontWeight: 600, color: "var(--text)" }}>{conn.workspace_name}</span>
                    </div>
                  )}
                  {conn.connected_at && (
                    <div>
                      <span style={{ color: "var(--text3)" }}>Conectado el: </span>
                      <span>{new Date(conn.connected_at).toLocaleDateString("es-ES")}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Action buttons */}
              <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end", gap: 8 }}>
                {conn ? (
                  <>
                    {onSync && (
                      <button
                        onClick={() => handleSync(provider)}
                        disabled={syncing === provider || isDisconnecting}
                        style={{
                          padding: "7px 16px",
                          borderRadius: 8,
                          border: "1px solid var(--border)",
                          background: "var(--surface)",
                          color: syncing === provider ? "var(--text3)" : "var(--accent)",
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: syncing === provider ? "not-allowed" : "pointer",
                          fontFamily: "inherit",
                          transition: "background 0.15s",
                        }}
                      >
                        {syncing === provider ? "Sincronizando…" : lastSynced[provider] ? "Sincronizado ✓" : "Sincronizar"}
                      </button>
                    )}
                    <button
                      onClick={() => handleDisconnect(provider)}
                      disabled={isDisconnecting || syncing === provider}
                      style={{
                        padding: "7px 16px",
                        borderRadius: 8,
                        border: "1px solid #fca5a5",
                        background: isDisconnecting ? "#fee2e2" : "#fff1f2",
                        color: isDisconnecting ? "#9ca3af" : "#dc2626",
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: isDisconnecting ? "not-allowed" : "pointer",
                        fontFamily: "inherit",
                        transition: "background 0.15s",
                      }}
                    >
                      {isDisconnecting ? "Desconectando…" : "Desconectar"}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleConnect(connectPath)}
                    style={{
                      padding: "7px 18px",
                      borderRadius: 8,
                      border: "none",
                      background: "var(--accent, #0ea5e9)",
                      color: "#fff",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      transition: "opacity 0.15s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                  >
                    Conectar
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer note */}
      <div style={{
        marginTop: 18,
        padding: "10px 14px",
        background: "var(--bg)",
        borderRadius: 9,
        border: "1px solid var(--border)",
        fontSize: 12,
        color: "var(--text3)",
        lineHeight: 1.5,
      }}>
        El calendario de Outlook es de solo lectura. En Notion sí se escribe: mover una tarjeta
        o editar una página desde Proyectos cambia la página en tu Notion.
      </div>

      {/* Close button */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
        <button
          onClick={onClose}
          style={{
            padding: "8px 20px",
            borderRadius: 9,
            border: "1px solid var(--border)",
            background: "var(--surface)",
            color: "var(--text2)",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Cerrar
        </button>
      </div>
    </div>
  );
}
